<template>
  <div class="st-surface">
    <StToolbar tone="canvas" class="st-surface__bar">
      <span class="st-surface__title">Regiments of Renown</span>
    </StToolbar>

    <div class="st-surface__body">
      <EditorSideList
        :items="ws.regimentsOfRenown.value"
        :selected-index="selectedIndex"
        add-label="Add Regiment"
        empty-hint="No regiments yet."
        width-key="studio:rorSidebarWidth"
        @select="selectedIndex = $event"
        @add="addingRegiment = true"
        @reorder="moveRegiment"
      >
        <template #row="{ item }">{{ (item as IRegimentOfRenown).name || '(unnamed)' }}</template>
      </EditorSideList>

      <template v-if="currentRegiment">
        <SegmentedSideList
          :sections="segSections"
          :army-label="currentRegiment.name"
          :army-selected="node.kind === 'regiment'"
          :selected-key="selectedSectionKey"
          :selected-index="'index' in node ? node.index : -1"
          width-key="studio:rorSegWidth"
          @select-army="node = { kind: 'regiment' }"
          @select="onSelectSection"
          @add="onAddSection"
          @reorder="onReorderSection"
        />

        <main class="st-surface__main st-surface__main--gap">
          <div v-if="node.kind === 'regiment'" class="st-editor-card">
            <TitleBar
              :name="currentRegiment.name"
              noun="Regiment"
              :entity-id="currentRegiment.id"
              id-prefix="ror"
              @rename="renameRegiment"
              @duplicate="duplicateRegiment"
              @delete="deleteRegiment"
              @update:entity-id="updateRegiment({ ...currentRegiment, id: $event })"
            >
              <template #tabs>
                <StSegmentedControl v-model="currentTab" :options="tabOptions" size="sm" on-shell />
              </template>
            </TitleBar>

            <div
              v-if="currentTab === 'details'"
              class="st-editor-card__pane st-editor-card__pane--pad"
            >
              <RegimentOfRenownDetails
                :model-value="currentRegiment"
                @update:model-value="updateRegiment"
              />
            </div>

            <div v-else class="st-editor-card__pane st-editor-card__pane--pad">
              <RegimentOfRenownPreview :regiment="currentRegiment" />
            </div>
          </div>

          <div v-else-if="currentUnit" class="st-editor-card">
            <UnitEditor
              :model-value="currentUnit.unit"
              :army-keyword="currentRegiment.sourceArmies[0]"
              @update:model-value="patchCurrentUnit({ unit: $event })"
              @rename="renameUnit"
              @duplicate="duplicateUnit"
              @delete="deleteUnit"
            >
              <template #meta>
                <div class="ror-unit-editor__meta">
                  <StField label="Count">
                    <StNumberInput
                      :model-value="currentUnit.count"
                      :min="0"
                      @update:model-value="patchCurrentUnit({ count: $event ?? 0 })"
                    />
                  </StField>
                  <StField label="Max">
                    <StNumberInput
                      :model-value="currentUnit.max"
                      :min="0"
                      @update:model-value="patchCurrentUnit({ max: $event ?? 0 })"
                    />
                  </StField>
                  <StField label="Entry points">
                    <StNumberInput
                      :model-value="currentUnit.points"
                      :min="0"
                      @update:model-value="patchCurrentUnit({ points: $event ?? 0 })"
                    />
                  </StField>
                </div>
              </template>
            </UnitEditor>
          </div>

          <div v-else class="st-surface__empty">Add a unit to this regiment.</div>
        </main>
      </template>

      <div v-else class="st-surface__placeholder">Select or add a regiment to begin.</div>
    </div>

    <NameModal
      :open="addingRegiment"
      title="New Regiment of Renown"
      placeholder="Regiment name..."
      @confirm="handleAddRegiment"
      @close="addingRegiment = false"
    />
    <NameModal
      :open="addingUnit"
      title="New Regiment Unit"
      placeholder="Unit name..."
      @confirm="handleAddUnit"
      @close="addingUnit = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  makeRegimentOfRenown,
  makeRegimentOfRenownUnit,
  makeRegimentOfRenownUpgrade,
  type IRegimentOfRenown,
  type IRegimentOfRenownUnit,
} from '@scrolldex/shared';
import {
  StField,
  StNumberInput,
  StSegmentedControl,
  StToolbar,
  type SegmentOption,
} from '../components/ui/index.js';
import EditorSideList from '../components/EditorSideList.vue';
import SegmentedSideList, { type SegmentSection } from '../components/SegmentedSideList.vue';
import TitleBar from '../components/editor/TitleBar.vue';
import NameModal from '../components/NameModal.vue';
import UnitEditor from '../components/army/UnitEditor.vue';
import RegimentOfRenownDetails from '../components/army/RegimentOfRenownDetails.vue';
import RegimentOfRenownPreview from '../components/army/RegimentOfRenownPreview.vue';
import { useWorkspace } from '../state/workspace.js';
import { useIndexedCollection } from '../composables/useIndexedCollection.js';
import { useEntityTabs } from '../composables/useEntityTabs.js';
import { uniqueName } from '../utils/uniqueName.js';

const ws = useWorkspace();
const addingRegiment = ref(false);
const addingUnit = ref(false);
const node = ref<{ kind: 'regiment' } | { kind: 'unit'; index: number }>({ kind: 'regiment' });

const regiments = useIndexedCollection<IRegimentOfRenown>({
  items: () => ws.regimentsOfRenown.value,
  setItems: (next) => ws.setRegimentsOfRenown(next),
});
const {
  selectedIndex,
  current: currentRegiment,
  updateCurrent,
  append,
  deleteCurrent,
  insertAfterCurrent,
  move,
} = regiments;

const tabOptions: SegmentOption[] = [
  { value: 'details', label: 'Details', icon: 'file-pen' },
  { value: 'preview', label: 'Preview', icon: 'eye' },
];

const { currentTab, setTab } = useEntityTabs<'details' | 'preview'>(
  () => currentRegiment.value?.id,
  'details',
);

const segSections = computed<SegmentSection[]>(() => {
  const regiment = currentRegiment.value;
  if (!regiment) return [];
  return [
    {
      key: 'unit',
      label: 'Units',
      items: regiment.units.map((entry) => ({ name: entry.unit.name })),
      emptyHint: 'No units yet.',
    },
  ];
});

const selectedSectionKey = computed<string | null>(() =>
  node.value.kind === 'unit' ? 'unit' : null,
);

const currentUnit = computed<IRegimentOfRenownUnit | null>(() => {
  const units = currentRegiment.value?.units ?? [];
  return node.value.kind === 'unit' ? (units[node.value.index] ?? null) : null;
});

watch(
  () => currentRegiment.value?.id,
  () => {
    node.value = { kind: 'regiment' };
  },
);

function normalized(regiment: IRegimentOfRenown): IRegimentOfRenown {
  return {
    ...regiment,
    upgrades: [
      {
        ...(regiment.upgrades[0] ?? makeRegimentOfRenownUpgrade(regiment.name)),
        name: `Regiment of Renown: ${regiment.name}`,
      },
    ],
  };
}

function updateRegiment(regiment: IRegimentOfRenown) {
  updateCurrent(normalized(regiment));
}

function renameRegiment(name: string) {
  const regiment = currentRegiment.value;
  if (!regiment) return;
  updateRegiment({ ...regiment, name });
}

function handleAddRegiment(name: string) {
  const regiment = makeRegimentOfRenown(name);
  append(regiment);
  setTab(regiment.id, 'details');
  node.value = { kind: 'regiment' };
  addingRegiment.value = false;
}

function deleteRegiment() {
  deleteCurrent();
}

function duplicateRegiment() {
  const regiment = currentRegiment.value;
  if (!regiment) return;
  const copy = {
    ...JSON.parse(JSON.stringify(regiment)),
    name: uniqueName(
      regiment.name,
      ws.regimentsOfRenown.value.map((ror) => ror.name),
    ),
  } as IRegimentOfRenown;
  copy.id = `${copy.id}-copy-${Date.now().toString(36)}`;
  copy.upgrades = [{ ...copy.upgrades[0], name: `Regiment of Renown: ${copy.name}` }];
  insertAfterCurrent(copy);
  setTab(copy.id, 'details');
  node.value = { kind: 'regiment' };
}

function moveRegiment(from: number, to: number) {
  move(from, to);
}

function updateUnits(units: IRegimentOfRenownUnit[]) {
  const regiment = currentRegiment.value;
  if (!regiment) return;
  updateRegiment({ ...regiment, units });
  if (node.value.kind === 'unit' && node.value.index >= units.length) {
    node.value = units.length ? { kind: 'unit', index: units.length - 1 } : { kind: 'regiment' };
  }
}

function patchCurrentUnit(part: Partial<IRegimentOfRenownUnit>) {
  const regiment = currentRegiment.value;
  if (!regiment || node.value.kind !== 'unit') return;
  updateUnits(
    regiment.units.map((entry, i) => (i === node.value.index ? { ...entry, ...part } : entry)),
  );
}

function handleAddUnit(name: string) {
  const regiment = currentRegiment.value;
  if (!regiment) return;
  const units = [...regiment.units, makeRegimentOfRenownUnit(name)];
  updateUnits(units);
  node.value = { kind: 'unit', index: units.length - 1 };
  addingUnit.value = false;
}

function renameUnit(name: string) {
  const entry = currentUnit.value;
  if (!entry) return;
  patchCurrentUnit({ unit: { ...entry.unit, name } });
}

function duplicateUnit() {
  const regiment = currentRegiment.value;
  const entry = currentUnit.value;
  if (!regiment || !entry) return;
  const copy = JSON.parse(JSON.stringify(entry)) as IRegimentOfRenownUnit;
  copy.unit.name = uniqueName(
    entry.unit.name,
    regiment.units.map((unitEntry) => unitEntry.unit.name),
  );
  const units = [...regiment.units];
  const index = node.value.kind === 'unit' ? node.value.index : regiment.units.length - 1;
  units.splice(index + 1, 0, copy);
  updateUnits(units);
  node.value = { kind: 'unit', index: index + 1 };
}

function deleteUnit() {
  const regiment = currentRegiment.value;
  if (!regiment || node.value.kind !== 'unit') return;
  updateUnits(regiment.units.filter((_, i) => i !== node.value.index));
}

function moveUnit(from: number, to: number) {
  const regiment = currentRegiment.value;
  if (!regiment) return;
  const units = [...regiment.units];
  const [moved] = units.splice(from, 1);
  units.splice(to, 0, moved);
  updateUnits(units);
  if (node.value.kind === 'unit' && node.value.index === from) {
    node.value = { kind: 'unit', index: to };
  }
}

function onSelectSection(key: string, index: number) {
  if (key === 'unit') node.value = { kind: 'unit', index };
}

function onAddSection(key: string) {
  if (key === 'unit') addingUnit.value = true;
}

function onReorderSection(key: string, from: number, to: number) {
  if (key === 'unit') moveUnit(from, to);
}
</script>

<style scoped>
.ror-unit-editor__meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 9rem));
  gap: var(--st-space-3);
  padding: var(--st-space-3);
}
</style>
